// store/inboxCounts.js
import { defineStore } from 'pinia';
import { useUserStore } from '@store/user';

const REFETCH_DEBOUNCE_MS = 750;
const SAFETY_POLL_MS = 300000; // websocket-drop backstop

export const useInboxCountsStore = defineStore('inboxCounts', {
    state: () => ({
        text: 0,
        phone: 0,
        isFetching: false,
        error: null,
        listener: null,
        listenerTenantId: null,
        _debounceTimer: null,
        _pollTimer: null,
    }),
    actions: {
        async fetchCounts() {
            const userStore = useUserStore();
            const teamParams =
                userStore.getTeam != 'NONE' && userStore.getTeam ?
                    {
                        team: userStore.getTeam,
                    }
                :   {};

            this.isFetching = true;
            this.error = null;
            try {
                // xSilent: badge polls must not toast / redirect on transient failures
                // (Wi‑Fi blips still surface as Axios ERR_NETWORK via the interceptor).
                const [{ data: text }, { data: phone }] = await Promise.all([
                    window.tenantAxios.get('inbox', {
                        xSilent: true,
                        params: {
                            count: 1,
                            direction: 'INCOMING',
                            seen: 0,
                            type: 'TEXT',
                            ...teamParams,
                        },
                    }),
                    window.tenantAxios.get('inbox', {
                        xSilent: true,
                        params: {
                            count: 1,
                            direction: 'INCOMING',
                            seen: 0,
                            type: 'PHONE',
                            ...teamParams,
                        },
                    }),
                ]);

                this.text = text.count;
                this.phone = phone.count;
            } catch (err) {
                console.error('Error fetching inbox counts:', err);
                this.error = err;
            } finally {
                this.isFetching = false;
            }
        },

        startListener() {
            const userStore = useUserStore();
            const tenantId = userStore.getTenant?.id;
            if (!window.echo || !tenantId) {
                return;
            }

            if (this.listenerTenantId === tenantId && this.listener) {
                return;
            }

            this.listenerTenantId = tenantId;
            this.listener = window.echo.private(`tenant.${tenantId}`).listen('InboxCountsChangedEvent', () => {
                if (this._debounceTimer) {
                    clearTimeout(this._debounceTimer);
                }
                this._debounceTimer = setTimeout(() => {
                    this.fetchCounts();
                }, REFETCH_DEBOUNCE_MS);
            });

            if (this._pollTimer) {
                clearInterval(this._pollTimer);
            }
            this._pollTimer = setInterval(() => {
                this.fetchCounts();
            }, SAFETY_POLL_MS);
        },

        stopListener() {
            // Don't echo.leave(`tenant.…`) here — the channel is shared with the
            // jobCounts FixList listener; user.js tears it down via leaveAllChannels().
            this.listener = null;
            this.listenerTenantId = null;
            if (this._debounceTimer) {
                clearTimeout(this._debounceTimer);
                this._debounceTimer = null;
            }
            if (this._pollTimer) {
                clearInterval(this._pollTimer);
                this._pollTimer = null;
            }
        },

        resetCounts() {
            this.text = 0;
            this.phone = 0;
        },
    },
    getters: {
        getTextCount: (state) => state.text,
        getPhoneCount: (state) => state.phone,
        getIsFetching: (state) => state.isFetching,
        getError: (state) => state.error,
    },
});
